/**
 * DORMANT PAGE — not reachable.
 *
 * Password reset currently happens on Firebase's hosted page (the link in the
 * reset email points there), so this route redirects away in `beforeLoad` and
 * its reset logic (code verification, saving the new password) has been
 * removed. Only the UI is kept, in case we someday switch to our own reset
 * password page. To revive it: drop the redirect and wire the form back up
 * with react-hook-form + firebaseAuth.verifyPasswordResetCode /
 * confirmPasswordReset (see git history).
 */
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { AppShell } from '@tupai/shell';
import hello from '@tupai/tokens/assets/spot/hello.svg';
import PasswordInput from '@/components/password-input';
import { useTranslation } from '@tupai/i18n/react';

export const Route = createFileRoute('/reset-password')({
  beforeLoad: () => {
    throw redirect({ to: '/login', replace: true });
  },
  component: ResetPasswordPage,
});

const inputClass =
  'h-12 w-full rounded-md border-[1.5px] border-border bg-surface px-4 py-3 text-content placeholder-hint outline-none focus:border-primary transition-colors';

function ResetPasswordPage() {
  const navigate = useNavigate();
  const { t } = useTranslation('auth');
  // Static placeholders for the dormant UI; nothing drives these any more.
  const success = false;
  const email = '';

  return (
    <AppShell
      variant="fullscreen"
      tone="surface"
      className="relative items-center justify-center overflow-y-auto px-8 py-8"
    >
      <div className="flex w-full max-w-sm flex-col">
        <div className="mb-8 flex justify-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-pill bg-bg text-primary">
            <img src={hello} alt="" className="w-12 h-12" style={{ objectFit: 'contain' }} />
          </div>
        </div>
        <h1 className="mb-8 font-display text-2xl text-content text-center">
          {t('setNewPasswordTitle')}
        </h1>

        {success ? (
          <div className="text-center">
            <p className="mb-6 text-sm text-content">{t('passwordResetSuccess')}</p>
            <button onClick={() => navigate({ to: '/login' })} className="cta-primary w-full">
              {t('logIn')}
            </button>
          </div>
        ) : (
          <form className="flex flex-col gap-5" onSubmit={(e) => e.preventDefault()}>
            {email && (
              <p className="text-sm text-content text-center mb-4">
                {t('resettingPasswordFor')} <strong>{email}</strong>
              </p>
            )}
            <div>
              <label className="text-sm font-bold text-muted tracking-wide mb-1.5 block">
                {t('newPasswordLabel')}
              </label>
              <PasswordInput
                name="password"
                autoComplete="new-password"
                className={inputClass}
                placeholder={t('passwordSignupPlaceholder')}
              />
            </div>
            <div>
              <label className="text-sm font-bold text-muted tracking-wide mb-1.5 block">
                {t('confirmNewPasswordLabel')}
              </label>
              <PasswordInput
                name="confirmPassword"
                autoComplete="new-password"
                className={inputClass}
                placeholder={t('passwordSignupPlaceholder')}
              />
            </div>
            <div className="mt-2 flex flex-col gap-4">
              <button type="submit" className="cta-primary disabled:opacity-40">
                {t('savePassword')}
              </button>
              <button
                type="button"
                className="text-sm font-bold text-muted hover:text-primary transition-colors mt-2"
                onClick={() => navigate({ to: '/login' })}
              >
                {t('backToLogin')}
              </button>
            </div>
          </form>
        )}
      </div>
    </AppShell>
  );
}
